import {
  Deployment,
  DeploymentStatus,
  InputSourceType
} from '@/types/deployment'
import {
  DeploymentGroup,
  DeploymentGroupMember,
  DeploymentGroupMemberCounts,
  DeploymentOverviewApp,
  DeploymentOverviewDevice
} from '@/types/deploymentGroup'
import { Device, Gpu } from '@/types/device'
import { getDevicesCameras } from '@/utils/device/getDeviceAttr'

const CONNECTING_STATUSES = new Set<DeploymentStatus>([
  DeploymentStatus.InProgress,
  DeploymentStatus.Initializing,
  DeploymentStatus.LoadingModel,
  DeploymentStatus.PreparingOcr,
  DeploymentStatus.RegisteringMilestone,
  DeploymentStatus.ConnectingVideoStream,
  DeploymentStatus.DownloadingConfig,
  DeploymentStatus.DownloadingModel,
  DeploymentStatus.CheckingDockerManifest,
  DeploymentStatus.DownloadingImage,
  DeploymentStatus.Recovering,
  DeploymentStatus.QueuedForEngineBuild,
  DeploymentStatus.BuildingEngine
])

const STOPPED_STATUSES = new Set<DeploymentStatus>([
  DeploymentStatus.Stopping,
  DeploymentStatus.Stopped
])

export type MemberSummary = DeploymentGroupMemberCounts & {
  total: number
}

export const summarizeMembers = (
  members: DeploymentGroupMember[]
): MemberSummary => {
  const summary: MemberSummary = {
    total: members.length,
    running: 0,
    connecting: 0,
    failed: 0,
    stopped: 0,
    other: 0
  }

  members.forEach(({ status }) => {
    if (status === DeploymentStatus.Running) {
      summary.running += 1
    } else if (status === DeploymentStatus.Failed) {
      summary.failed += 1
    } else if (CONNECTING_STATUSES.has(status)) {
      summary.connecting += 1
    } else if (STOPPED_STATUSES.has(status)) {
      summary.stopped += 1
    } else {
      summary.other += 1
    }
  })

  return summary
}

const compareGroupsByAge = (a: DeploymentGroup, b: DeploymentGroup) => {
  return a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id)
}

export const getGroupLabel = (
  group: DeploymentGroup,
  deviceGroups: DeploymentGroup[]
) => {
  if (group.display_number) {
    return group.display_number
  }

  const position = [...deviceGroups]
    .sort(compareGroupsByAge)
    .findIndex((candidate) => candidate.id === group.id)

  return position === -1 ? 1 : position + 1
}

export const sortGroupsByLabel = (groups: DeploymentGroup[]) => {
  return [...groups].sort(
    (a, b) =>
      getGroupLabel(a, groups) - getGroupLabel(b, groups) ||
      compareGroupsByAge(a, b)
  )
}

export const sortDevicesByLabel = (
  devices: DeploymentOverviewDevice[],
  devicesById: Record<string, Device | undefined>
) => {
  const labelOf = (device: DeploymentOverviewDevice) =>
    devicesById[device.device_id]?.label || device.device_id

  return [...devices].sort((a, b) => labelOf(a).localeCompare(labelOf(b)))
}

export type OverviewGroupMatch = {
  app: DeploymentOverviewApp
  overviewDevice: DeploymentOverviewDevice
  group: DeploymentGroup
  label: number
}

export const findOverviewGroup = (
  apps: DeploymentOverviewApp[],
  groupId: string
): OverviewGroupMatch | null => {
  for (const app of apps) {
    for (const overviewDevice of app.devices) {
      const group = overviewDevice.groups.find((g) => g.id === groupId)
      if (group) {
        return {
          app,
          overviewDevice,
          group,
          label: getGroupLabel(group, overviewDevice.groups)
        }
      }
    }
  }
  return null
}

export const groupContainsStream = (
  group: DeploymentGroup,
  streamId: string
) => {
  return group.members.some((member) => member.stream_id === streamId)
}

export type SingleDeploymentGpu =
  { kind: 'gpu'; index: number; name?: string } | { kind: 'device' } | null

export const getSingleCameraReasons = (
  deployment: Pick<
    Deployment,
    'group_exclusion_reasons' | 'single_camera_reasons'
  >
) =>
  deployment.group_exclusion_reasons ?? deployment.single_camera_reasons ?? []

export const resolveSingleDeploymentGpu = (
  deployment: Deployment,
  deviceGpus: Gpu[]
): SingleDeploymentGpu => {
  if (deployment.gpu) {
    return {
      kind: 'gpu',
      index: deployment.gpu.index,
      name: deployment.gpu.name
    }
  }

  const { gpu_index, allowed_gpus } = deployment

  if (allowed_gpus && allowed_gpus.length !== 1) {
    return { kind: 'device' }
  }

  if (typeof gpu_index !== 'number') {
    return null
  }

  return {
    kind: 'gpu',
    index: gpu_index,
    name: deviceGpus.find((gpu) => gpu.index === gpu_index)?.name ?? undefined
  }
}

export const getMemberCameraName = (
  member: DeploymentGroupMember,
  device?: Device
) => {
  const camera = (device ? getDevicesCameras(device) : []).find(
    ({ camera_id }) => camera_id === member.stream_id
  )

  return camera?.camera_name ?? member.stream_id
}

export const getGroupCameraNames = (
  group: DeploymentGroup,
  device?: Device
) => {
  return [...group.members]
    .sort((a, b) => a.stream_index - b.stream_index)
    .map((member) => getMemberCameraName(member, device))
}

export const summarizeApp = (app: DeploymentOverviewApp) => {
  const summary = { cameras: 0, groups: 0, ownContainer: 0, folders: 0 }

  app.devices.forEach(({ groups, single_deployments }) => {
    summary.groups += groups.length
    summary.cameras += groups.reduce((n, group) => n + group.members.length, 0)

    single_deployments.forEach((deployment) => {
      if (
        deployment.settings_payload?.input_source_type ===
        InputSourceType.MediaFolder
      ) {
        summary.folders += 1
      } else {
        summary.cameras += 1
        summary.ownContainer += 1
      }
    })
  })

  return summary
}
